import { useEffect, useState } from "react";
import { Plus, Upload, FileText, X, Trash2, Pencil, ScanLine, AlertCircle, Printer, Download } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { PageHeader } from "../components/ui/PageHeader";
import { DataTable, type Column } from "../components/ui/DataTable";
import { TableExportToolbar } from "../components/TableExportToolbar";
import { PrintTable } from "../components/PrintTable";
import { PreviewModal } from "../components/PreviewModal";
import { Lightbox } from "../components/ui/Lightbox";
import { ScanImportButton } from "../components/scan/ScanImportButton";
import { ScanImportModal } from "../components/scan/ScanImportModal";
import { CurrencySelect } from "../components/ui/CurrencySelect";
import { Select } from "../components/ui/Select";
import { eur, fmtCurrency, fmtDate, fmtTime, nlStatus } from "../lib/format";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/** Categorie-suggesties uit de OCR-tekst: eerste match op de tekst wint. */
const CATEGORIE_REGELS: [RegExp, string][] = [
  [/boekhouding|accountant|accounting|salaris|loonheffing|payroll/i, "Boekhouding"],
  [/administratie|secretariaat/i, "Administratie"],
  [/software|abonnement|subscription|hosting|saas|licentie/i, "Software"],
  [/kantoor|papier|print(en)?|benodigdheden/i, "Kantoor"],
  [/reizen|hotel|vliegtuig|trein|parkeren|brandstof|tanken/i, "Reizen & vervoer"],
  [/restaurant|lunch|diner|kantine|borrel/i, "Representatie"],
  [/telefoon|telecom|internet|mobiel|sim-?kaart/i, "Telecom"],
  [/verzekering|insurance|premie/i, "Verzekering"],
  [/marketing|advertentie|reclame|website|drukwerk/i, "Marketing"],
  [/abonnementskosten|lidmaatschap|contributie/i, "Lidmaatschap"],
];

interface Expense {
  id: string;
  description: string;
  merchant?: string;
  date: string;
  amountInclVat: number;
  vatAmount?: number;
  receiptUrl?: string;
  ocrStatus?: string;
  currency?: string;
  exchangeRate?: number;
  amountEurInclVat?: number;
  amountEurExclVat?: number;
  vatAmountEur?: number;
  lines?: { id: string; description?: string | null; amountInclVat: number; vatAmount?: number | null; vatRateId?: string | null; vatRate?: { id: string; name: string; percentage: number } | null }[];
  /** Gevuld bij auto-geboekte uitgaven (te-betalen-factuur → 'Markeer als
   *  betaald'): de platformfactuur-PDF dient dan als document. */
  platformInvoice?: { id: string; number: string } | null;
  /** Gevuld bij contant betaalde uitgaven (kasboek-koppeling): geldt als
   *  betaald en telt niet als openstaande crediteur. */
  cashBookEntryId?: string | null;
  bankTransactionId?: string | null;
}

/** Een btw-regel in de regel-editor (multi-btw per uitgave). */
interface AmountLine {
  amount: string; // bedrag incl. BTW (gebruiker voert in)
  vatRateId: string; // btw-soort
  description?: string;
}

export default function Expenses() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "SUPERADMIN";
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [scanning, setScanning] = useState(false);
  // Verstreken OCR-seconden voor de voortgangsbalk (geen echte % beschikbaar:
  // één upload-antwoord — de balk loopt vloeiend op en stopt bij 92%).
  const [ocrSec, setOcrSec] = useState(0);
  useEffect(() => {
    if (!scanning) return;
    const t0 = Date.now();
    setOcrSec(0);
    const iv = setInterval(() => setOcrSec(Math.floor((Date.now() - t0) / 1000)), 500);
    return () => clearInterval(iv);
  }, [scanning]);
  const [ocrError, setOcrError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const [description, setDescription] = useState("");
  const [merchant, setMerchant] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  // Multi-valuta: valuta + wisselkoers (EUR per 1 eenheid currency).
  const [currency, setCurrency] = useState("EUR");
  const [exchangeRate, setExchangeRate] = useState<string>("1");
  const [rateLoading, setRateLoading] = useState(false);
  // Boekhoud-velden die in de edit-modal bewerkbaar moeten zijn: leverancier,
  // categorie + notities. BTW-soort zit per regel (lines), zodat een uitgave
  // meerdere btw-tarieven kan hebben (multi-btw).
  const [contactId, setContactId] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [contacts, setContacts] = useState<any[]>([]);
  const [vatRates, setVatRates] = useState<any[]>([]);
  // Per-regel btw (multi-btw): minstens één regel; een enkele regel = een
  // gewone enkele-bedrag uitgave (backward-compat UX).
  const [lines, setLines] = useState<AmountLine[]>([{ amount: "", vatRateId: "" }]);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [ocrText, setOcrText] = useState("");
  const [aiFilled, setAiFilled] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [visibleRows, setVisibleRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; fileName: string } | null>(null);

  useEffect(() => {
    loadExpenses();
    api.get("/api/contacts").then((res) => setContacts(res.data)).catch(() => {});
    api.get("/api/vat-rates").then((res) => {
      setVatRates(res.data);
      const def = res.data.find((v: any) => Number(v.percentage) === 21)?.id || res.data[0]?.id || "";
      setLines([{ amount: "", vatRateId: def }]);
    }).catch(() => {});
  }, []);

  /** Btw-percentage voor een vatRateId (uit de geladen vatRates). */
  const vatPctFor = (vid: string): number => {
    const v = vatRates.find((r) => r.id === vid);
    return v ? Number(v.percentage) : 0;
  };

  /** Parse een bedrag: Nederlands ("2.178,00" → 2178) óf een JSON-nummer als
   *  string ("217.8" → 217.8 — decimale punt, dus NIET als duizendtalscheider
   *  wegstrippen; daar ging het bruto/netto ×10 fout). */
  const parseAmt = (s: string): number => {
    const raw = String(s).trim();
    if (/^-?\d+(\.\d{1,2})?$/.test(raw)) return parseFloat(raw) || 0;
    const n = parseFloat(raw.replace(/\./g, "").replace(",", "."));
    return isNaN(n) ? 0 : n;
  };

  /** Netto + BTW voor één regel (bedrag is incl. BTW). */
  const lineCalc = (line: AmountLine) => {
    const incl = parseAmt(line.amount);
    const pct = vatPctFor(line.vatRateId);
    const netto = pct > 0 ? incl / (1 + pct / 100) : incl;
    const btw = incl - netto;
    return { incl, netto, btw };
  };

  /** Totalen over alle regels (bruto / netto / btw). */
  const totals = lines.reduce(
    (acc, l) => {
      const c = lineCalc(l);
      acc.bruto += c.incl;
      acc.netto += c.netto;
      acc.btw += c.btw;
      return acc;
    },
    { bruto: 0, netto: 0, btw: 0 },
  );

  const updateLine = (i: number, patch: Partial<AmountLine>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () =>
    setLines((prev) => [...prev, { amount: "", vatRateId: vatRates.find((v: any) => Number(v.percentage) === 21)?.id || vatRates[0]?.id || "" }]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const vatRateOptions = [
    { value: "", label: "Geen BTW (0%)" },
    ...vatRates.map((v: any) => ({ value: v.id, label: `${Number(v.percentage)}%${v.name ? ` — ${v.name}` : ""}` })),
  ];

  const fmtCur = (n: number) => fmtCurrency(n, currency);
  const rateNum = Number(exchangeRate) || 1;
  const isForeign = currency && currency !== "EUR";
  const brutoEur = isForeign ? totals.bruto * rateNum : totals.bruto;

  const loadExpenses = () => api.get("/api/expenses").then((res) => setExpenses(res.data));

  /** Haal de EUR-koers op voor een valuta op een bepaalde datum (frankfurter).
   *  Vul het editable koers-veld in. Bij falen blijft de huidige waarde staan. */
  const fetchRate = async (cur: string, rateDate: string) => {
    if (!cur || cur.toUpperCase() === "EUR") { setExchangeRate("1"); return; }
    setRateLoading(true);
    try {
      const { data } = await api.get("/api/exchange-rate", { params: { from: cur, date: rateDate } });
      if (typeof data?.rate === "number" && data.rate > 0) setExchangeRate(String(data.rate));
    } catch { /* laat bestaande/handmatige waarde staan */ }
    finally { setRateLoading(false); }
  };

  /** Bij wisselen van valuta: koers ophalen (behalve bij EUR → 1). */
  const onCurrencyChange = (cur: string) => {
    setCurrency(cur);
    fetchRate(cur, date);
  };

  const reset = () => {
    setDescription("");
    setMerchant("");
    setDate(new Date().toISOString().split("T")[0]);
    setCurrency("EUR");
    setExchangeRate("1");
    setRateLoading(false);
    setContactId("");
    setCategory("");
    setNotes("");
    setLines([{ amount: "", vatRateId: vatRates.find((v: any) => Number(v.percentage) === 21)?.id || vatRates[0]?.id || "" }]);
    setFile(null);
    setPreviewUrl(null);
    setOcrText("");
    setAiFilled(new Set());
    setOcrError("");
    setEditing(null);
    setSaveError("");
  };

  const handleFile = async (selectedFile: File) => {
    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
    setScanning(true);
    setOcrError("");
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      const { data } = await api.post("/api/ocr/receipt", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const filled = new Set<string>();
      if (!data.success) {
        setOcrError(data.error || "OCR kon het bonnetje niet lezen. Vul de velden handmatig in.");
      } else {
        if (data.merchant) {
          setMerchant(data.merchant);
          filled.add("merchant");
        }
        if (data.description) {
          setDescription(data.description);
          filled.add("description");
        }
        if (data.date) {
          setDate(data.date);
          filled.add("date");
        }
        // OCR levert getallen (JSON-nummers); zet ze naar Nederlandse invoer
        // ("217,80") zodat parseAmt en de weergave kloppen.
        const ocrBedrag = (n: any): string =>
          n == null || !isFinite(Number(n)) ? "" : String(Number(n).toFixed(2)).replace(".", ",");
        // Per-regel btw (multi-btw): als OCR vatLines herkent, vul de
        // regel-editor daarmee; match btw-percentage op een geladen vatRate.
        // Enkelvoudige fallback op totalAmount (oudere OCR-response).
        const matchVatByPct = (pct: number | null | undefined) => {
          if (pct == null) return "";
          const v = vatRates.find((r: any) => Number(r.percentage) === pct);
          return v?.id || "";
        };
        const defVat21 = matchVatByPct(21) || vatRates[0]?.id || "";
        if (Array.isArray(data.vatLines) && data.vatLines.length > 0) {
          setLines(data.vatLines.map((vl: any) => ({
            amount: ocrBedrag(vl.amountInclVat),
            vatRateId: matchVatByPct(vl.vatRatePercentage) || (data.vatAmount > 0 || vl.vatAmount > 0 ? defVat21 : ""),
            description: vl.description || "",
          })));
          filled.add("amount");
        } else if (typeof data.totalAmount === "number" && data.totalAmount > 0) {
          // BTW-soort: OCR-percentage als het matcht; anders bij een factuur
          // mét btw-bedrag de gangbare 21% (NL-standaard), anders leeg.
          const pct = matchVatByPct(data.vatRatePercentage);
          const rateId = pct || ((data.vatAmount ?? 0) > 0 ? defVat21 : "");
          setLines([{ amount: ocrBedrag(data.totalAmount), vatRateId: rateId }]);
          filled.add("amount");
        }
        if (data.currency) {
          setCurrency(String(data.currency));
          filled.add("currency");
        }
        // Categorie: suggestie op basis van de OCR-tekst (NL-keywords),
        // alleen als de gebruiker nog niets heeft ingevuld.
        if (!category) {
          const tekst = `${data.merchant || ""} ${data.description || ""} ${data.rawText || ""}`;
          const hit = CATEGORIE_REGELS.find(([re]) => re.test(tekst));
          if (hit) { setCategory(hit[1]); filled.add("category"); }
        }
        // Notities: omschrijving + periode + klant- en factuurnummer uit de
        // factuur, zodat alle referentiegegevens bij elkaar staan.
        const notitieRegels: string[] = [];
        if (data.description) notitieRegels.push(String(data.description));
        if (data.periode) notitieRegels.push(`Periode: ${data.periode}`);
        if (data.klantnummer) notitieRegels.push(`Klantnummer: ${data.klantnummer}`);
        if (data.reference) notitieRegels.push(`Factuurnummer: ${data.reference}`);
        if (notitieRegels.length) { setNotes(notitieRegels.join("\n")); filled.add("notes"); }
      }
      setAiFilled(filled);
      setOcrText(data.rawText || "");
      // Koers ophalen voor de (eventueel door OCR bepaalde) valuta op de datum.
      const cur = data.currency ? String(data.currency) : "EUR";
      const rateDate = data.date || date;
      if (cur.toUpperCase() !== "EUR") fetchRate(cur, rateDate);
    } catch (err: any) {
      setOcrError(err.response?.data?.error || "OCR-service niet bereikbaar. Vul handmatig in.");
    } finally {
      setScanning(false);
    }
  };

  // Geen useCallback met lege deps: die pakte een oudere handleFile-snapshot
  // (met lege vatRates), waardoor bij slepen de BTW-soort altijd leeg bleef.
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSave = async () => {
    setSaveError("");
    if (!description.trim()) { setSaveError("Vul een omschrijving in."); return; }
    if (!(totals.bruto > 0)) { setSaveError("Vul minstens één bedrag groter dan 0 in."); return; }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("description", description);
      formData.append("merchant", merchant);
      formData.append("date", date);
      // Per-regel btw (multi-btw): stuur de regels als JSON; de backend
      // persisteert per-regel btw-soort + herberekent header-totalen. Totals
      // gaan als amountInclVat/vatAmount mee voor backward-compat.
      const validLines = lines
        .filter((l) => parseAmt(l.amount) > 0)
        .map((l) => ({ amountInclVat: parseAmt(l.amount), vatRateId: l.vatRateId || undefined, description: l.description || undefined }));
      formData.append("lines", JSON.stringify(validLines));
      formData.append("amountInclVat", String(totals.bruto.toFixed(2)));
      formData.append("vatAmount", String(totals.btw.toFixed(2)));
      formData.append("currency", currency || "EUR");
      if (Number(exchangeRate) > 0) formData.append("exchangeRate", exchangeRate);
      // Altijd meesturen (ook ""), zodat de PATCH onderscheid kan maken tussen
      // "niet meegestuurd" (behoud) en "expliciet leeg" (wissen).
      formData.append("contactId", contactId || "");
      formData.append("category", category || "");
      formData.append("notes", notes || "");
      if (file) formData.append("receipt", file);

      if (editing) {
        await api.patch(`/api/expenses/${editing.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/api/expenses", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setIsOpen(false);
      reset();
      loadExpenses();
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || "Opslaan mislukt. Controleer de velden en probeer opnieuw.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Weet je zeker dat je deze uitgave wilt verwijderen?")) return;
    await api.delete(`/api/expenses/${id}`);
    loadExpenses();
  };

  const handleViewPdf = async (e: Expense) => {
    try {
      const res = await api.get(`/api/expenses/${e.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert("PDF laden mislukt.");
    }
  };

  const handleDownloadPdf = async (e: Expense) => {
    try {
      const res = await api.get(`/api/expenses/${e.id}/pdf`, { responseType: "blob", params: { download: "1" } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `uitgave.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert("PDF downloaden mislukt.");
    }
  };

  const openNew = () => {
    reset();
    setIsOpen(true);
  };

  const openEdit = (e: Expense) => {
    setEditing(e);
    setDescription(e.description);
    setMerchant(e.merchant || "");
    setDate(new Date(e.date).toISOString().split("T")[0]);
    setCurrency(e.currency || "EUR");
    setExchangeRate(e.exchangeRate != null ? String(e.exchangeRate) : "1");
    setContactId((e as any).contactId || (e as any).contact?.id || "");
    setCategory((e as any).category || "");
    setNotes((e as any).notes || "");
    // Regels laden: als de uitgave per-regel btw heeft (lines), gebruik die;
    // anders afleiden: één regel op basis van het header-bedrag + btw-soort.
    if (e.lines && e.lines.length > 0) {
      setLines(e.lines.map((l) => ({
        amount: String(l.amountInclVat),
        vatRateId: l.vatRateId || l.vatRate?.id || "",
        description: l.description || "",
      })));
    } else {
      setLines([{ amount: String(e.amountInclVat), vatRateId: (e as any).vatRate?.id || "" }]);
    }
    setPreviewUrl(e.receiptUrl ? `${import.meta.env.VITE_API_URL}${e.receiptUrl}` : null);
    setFile(null);
    setIsOpen(true);
  };

  const columns: Column<any>[] = [
    { key: "type", header: "Type", accessor: () => "Uitgave", sortable: false,
      render: () => <Badge className="bg-amber-50/80 text-amber-700 border-amber-200">Uitgave</Badge> },
    { key: "date", header: "Datum", accessor: (e) => e.date ? fmtDate(e.date) : "—",
      sortValue: (e) => new Date(e.date).getTime() },
    { key: "time", header: "Tijd", accessor: (e) => e.createdAt ? fmtTime(e.createdAt) : "—",
      sortValue: (e) => new Date(e.createdAt).getTime(), sortable: false },
    { key: "party", header: "Leverancier",
      accessor: (e) => e.merchant || e.contact?.name || e.contact?.companyName || "—" },
    { key: "desc", header: "Omschrijving", accessor: (e) => e.description || "—" },
    { key: "gross", header: "Bruto",
      accessor: (e) => eur(e.amountEurInclVat ?? e.amountInclVat), className: "text-right",
      sortValue: (e) => Number(e.amountEurInclVat ?? e.amountInclVat),
      render: (e) => (
        <div className="text-right leading-tight">
          <div>{fmtCurrency(e.amountInclVat, e.currency)}</div>
          {e.currency && e.currency !== "EUR" && e.amountEurInclVat != null && (
            <div className="text-xs text-slate-400">≈ {eur(e.amountEurInclVat)}</div>
          )}
        </div>
      ) },
    { key: "vat", header: "BTW",
      accessor: (e) => e.vatAmountEur != null ? eur(e.vatAmountEur) : (e.vatAmount != null ? eur(e.vatAmount) : "—"),
      className: "text-right",
      sortValue: (e) => Number(e.vatAmountEur ?? e.vatAmount) || 0,
      render: (e) => (
        <div className="text-right leading-tight">
          <div>{e.vatAmount != null ? fmtCurrency(e.vatAmount, e.currency) : "—"}</div>
          {e.currency && e.currency !== "EUR" && e.vatAmountEur != null && (
            <div className="text-xs text-slate-400">≈ {eur(e.vatAmountEur)}</div>
          )}
        </div>
      ) },
    { key: "net", header: "Netto",
      accessor: (e) => eur(e.amountEurExclVat != null ? e.amountEurExclVat : (Number(e.amountEurInclVat ?? e.amountInclVat) - Number(e.vatAmountEur ?? (e.vatAmount || 0)))),
      className: "text-right",
      sortValue: (e) => Number(e.amountEurExclVat != null ? e.amountEurExclVat : (Number(e.amountEurInclVat ?? e.amountInclVat) - Number(e.vatAmountEur ?? (e.vatAmount || 0)))),
      render: (e) => {
        const netExcl = e.amountExclVat != null ? Number(e.amountExclVat) : (Number(e.amountInclVat) - Number(e.vatAmount || 0));
        const netEur = e.amountEurExclVat != null ? Number(e.amountEurExclVat) : (Number(e.amountEurInclVat ?? e.amountInclVat) - Number(e.vatAmountEur ?? (e.vatAmount || 0)));
        return (
          <div className="text-right leading-tight">
            <div>{fmtCurrency(netExcl, e.currency)}</div>
            {e.currency && e.currency !== "EUR" && (
              <div className="text-xs text-slate-400">≈ {eur(netEur)}</div>
            )}
          </div>
        );
      } },
    { key: "vatType", header: "BTW-soort",
      accessor: (e) => {
        if (e.lines && e.lines.length > 1) {
          return e.lines.map((l: any) => `${l.vatRate?.percentage != null ? Number(l.vatRate.percentage) : 0}%`).join(" + ");
        }
        return e.vatRate?.percentage != null ? `${Number(e.vatRate.percentage)}%` : "—";
      } },
    { key: "status", header: "OCR-status", accessor: (e) => nlStatus(e.ocrStatus),
      render: (e) => (
        <div className="flex items-center gap-1.5">
          <Badge>{nlStatus(e.ocrStatus)}</Badge>
          {e.cashBookEntryId && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100/80 text-emerald-700 border border-emerald-200 text-[11px] font-semibold" title="Contant betaald via kasboekregel">
              kas
            </span>
          )}
          {e.bankTransactionId && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-sky-100/80 text-sky-700 border border-sky-200 text-[11px] font-semibold" title="Gekoppeld aan banktransactie">
              bank
            </span>
          )}
        </div>
      )},
    { key: "doc", header: "Document", accessor: () => "", sortable: false, width: "1%",
      render: (e) => e.receiptUrl ? (
        <button
          onClick={() => {
            const url = `${import.meta.env.VITE_API_URL}${e.receiptUrl}`;
            if (url.toLowerCase().endsWith(".pdf")) window.open(url, "_blank");
            else setLightbox({ src: url, fileName: e.receiptUrl?.split("/").pop() || "document" });
          }}
          className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
          title="Document bekijken"
        >
          {e.receiptUrl.toLowerCase().endsWith(".pdf") ? (
            <FileText size={22} className="text-slate-500" />
          ) : (
            <img src={`${import.meta.env.VITE_API_URL}${e.receiptUrl}`} alt="Bonnetje" className="h-10 w-10 object-cover rounded-md border border-slate-200" />
          )}
        </button>
      ) : e.platformInvoice ? (
        // Auto-geboekte uitgave: toon de bijbehorende platformfactuur-PDF
        // (server-side gegenereerd, via auth-endpoint — daarom als blob).
        <button
          onClick={async () => {
            try {
              const res = await api.get(`/api/platform-invoices/${e.platformInvoice!.id}/pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              window.open(url, "_blank");
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch {
              alert("PDF laden mislukt.");
            }
          }}
          className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
          title={`Inkoopfactuur ${e.platformInvoice.number} bekijken`}
        >
          <FileText size={22} className="text-slate-500" />
        </button>
      ) : <span className="text-slate-300">—</span> },
    { key: "actions", header: "Acties", accessor: () => "", sortable: false, width: "1%",
      render: (e) => (
        <div className="flex items-center gap-1">
          <button onClick={() => handleViewPdf(e)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600" title="PDF openen">
            <FileText size={21} />
          </button>
          <button onClick={() => handleDownloadPdf(e)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600" title="Download PDF">
            <Download size={21} />
          </button>
          <button onClick={() => setPreviewId(e.id)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600" title="Afdrukken / PDF">
            <Printer size={21} />
          </button>
          <button onClick={() => openEdit(e)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Bewerken">
            <Pencil size={21} />
          </button>
          {isSuperadmin && (
            <button onClick={() => handleDelete(e.id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-600" title="Verwijderen">
              <Trash2 size={21} />
            </button>
          )}
        </div>
      ) },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Uitgaven"
        subtitle="Upload bonnetjes en declareer kosten"
        action={
          <Button onClick={openNew}>
            <Plus size={27} /> Nieuwe uitgave
          </Button>
        }
      />

      <DataTable
        rows={expenses}
        columns={columns}
        rowKey={(e) => e.id}
        searchPlaceholder="Zoek uitgaven..."
        storageKey="expenses"
        initialSort={{ key: "date", dir: "desc" }}
        groupBy={{ getDate: (e) => e.date, getAmount: (e) => Number(e.amountEurInclVat ?? e.amountInclVat) }}
        emptyMessage="Nog geen uitgaven. Upload een bonnetje en laat de AI de velden invullen."
        onVisibleRowsChange={setVisibleRows}
        selectable
        onSelectionChange={setSelected}
        toolbar={
          <>
            <ScanImportButton onClick={() => setScanOpen(true)} />
            <TableExportToolbar
              rows={selected.length ? selected : visibleRows}
              columns={columns}
              filename={selected.length ? `uitgaven-selectie` : "uitgaven"}
              source="uitgaven"
              previewKind="expenses"
            />
          </>
        }
      />

      <PrintTable rows={selected.length ? selected : visibleRows} columns={columns} title="Uitgaven" />

      <Modal
        isOpen={isOpen}
        onClose={() => { setIsOpen(false); reset(); }}
        title={editing ? "Uitgave bewerken" : "Nieuwe uitgave"}
        subtitle={editing ? "Pas de gegevens aan" : "Upload een bonnetje of vul handmatig in"}
        size="lg"
      >
        <div className="space-y-5">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`relative flex flex-col items-center justify-center gap-3 w-full h-40 border-2 border-dashed rounded-2xl cursor-pointer transition-all overflow-hidden ${
              dragOver
                ? "border-brand-500 bg-brand-50/60"
                : "border-slate-300 bg-slate-50/60 hover:border-brand-400 hover:bg-brand-50/40"
            } ${scanning ? "scanner-ring" : ""}`}
          >
            {scanning ? (
              <div className="flex flex-col items-center text-brand-600 z-10">
                <ScanLine size={48} className="animate-bounce mb-2" />
                <span className="text-sm font-semibold">AI leest bonnetje...</span>
              </div>
            ) : previewUrl ? (
              <div className="relative w-full h-full">
                {file?.type === "application/pdf" || previewUrl.toLowerCase().endsWith(".pdf") ? (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    <FileText size={60} />
                  </div>
                ) : (
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setFile(null);
                    setPreviewUrl(null);
                  }}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-slate-900/70 text-white hover:bg-slate-900"
                >
                  <X size={24} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={48} className="text-slate-400" />
                <span className="text-sm text-slate-500 text-center px-6">
                  Sleep een bonnetje hierheen, of klik om te uploaden
                  <br />
                  <span className="text-xs">JPG, PNG of PDF</span>
                </span>
              </>
            )}
            <input
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </div>

          {scanning && (
            <div className="p-3 rounded-xl bg-brand-50/40 border border-brand-100">
              <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
                <span className="font-semibold">OCR leest het document…</span>
                <span>{ocrSec}s</span>
              </div>
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full bg-brand-500 transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(92, Math.round(100 * (1 - Math.exp(-ocrSec / 12))))}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Kan 10–60 seconden duren, afhankelijk van het aantal pagina's.</p>
            </div>
          )}

          {ocrError && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              <AlertCircle size={27} className="shrink-0 mt-0.5" />
              {ocrError}
            </div>
          )}

          <Input
            label="Omschrijving"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={aiFilled.has("description") ? "border-brand-300 bg-brand-50/30" : ""}
            required
          />
          <Input
            label="Merchant / winkel"
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
            className={aiFilled.has("merchant") ? "border-brand-300 bg-brand-50/30" : ""}
          />
          <Input
            label="Datum"
            type="date"
            value={date}
            onChange={(e) => { setDate(e.target.value); if (currency && currency !== "EUR") fetchRate(currency, e.target.value); }}
            className={aiFilled.has("date") ? "border-brand-300 bg-brand-50/30" : ""}
          />
          <Select
            label="Leverancier"
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            options={[
              { value: "", label: "Geen leverancier" },
              ...contacts.map((c: any) => ({ value: c.id, label: c.name || c.companyName || "Onbekend" })),
            ]}
          />
          <div className="grid grid-cols-2 gap-4">
            <CurrencySelect
              label="Valuta"
              value={currency}
              onChange={(e) => onCurrencyChange(e.target.value)}
            />
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Wisselkoers (EUR per 1 {currency})
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  disabled={currency === "EUR"}
                  className={aiFilled.has("currency") ? "border-brand-300 bg-brand-50/30" : ""}
                />
                {rateLoading && <span className="text-xs text-slate-400">…</span>}
              </div>
            </div>
          </div>
          {/* Bedrag-regels: meerdere bedragen met elk een btw-soort (multi-btw).
              Netto + btw worden automatisch berekend. Eén regel = gewone
              enkele-bedrag uitgave (backward-compat). */}
          <div className={`space-y-2 rounded-2xl border bg-slate-50/40 p-3 ${aiFilled.has("amount") ? "border-brand-300" : "border-slate-200"}`}>
            <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 px-1">
              <div className="col-span-5">Bedrag incl. BTW</div>
              <div className="col-span-4">BTW-soort</div>
              <div className="col-span-2">Netto / BTW</div>
              <div className="col-span-1"></div>
            </div>
            {lines.map((line, i) => {
              const c = lineCalc(line);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={line.amount}
                      onChange={(e) => updateLine(i, { amount: e.target.value })}
                      placeholder="0,00"
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                    />
                  </div>
                  <div className="col-span-4">
                    <select
                      value={line.vatRateId}
                      onChange={(e) => updateLine(i, { vatRateId: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                    >
                      {vatRateOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2 text-xs text-slate-500 leading-tight">
                    <div>netto {fmtCur(c.netto)}</div>
                    <div className="text-slate-400">btw {fmtCur(c.btw)}</div>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {lines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLine(i)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        title="Regel verwijderen"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <button
              type="button"
              onClick={addLine}
              className="flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:text-brand-700 px-1 pt-1"
            >
              <Plus size={18} /> Regel toevoegen
            </button>
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200 text-sm">
              <div><span className="text-slate-500">Bruto:</span> <span className="font-bold text-slate-800">{fmtCur(totals.bruto)}</span></div>
              <div><span className="text-slate-500">BTW:</span> <span className="font-semibold text-slate-700">{fmtCur(totals.btw)}</span></div>
              <div><span className="text-slate-500">Netto:</span> <span className="font-semibold text-slate-700">{fmtCur(totals.netto)}</span></div>
            </div>
            {isForeign && totals.bruto > 0 && rateNum > 0 && (
              <div className="text-xs text-slate-400 pt-1">≈ {eur(brutoEur)} in EUR</div>
            )}
          </div>
          <Input
            label="Categorie"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="bijv. Software, Kantoor, Reizen (optioneel)"
            className={aiFilled.has("category") ? "border-brand-300 bg-brand-50/30" : ""}
          />
          <Input
            label="Notities"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optioneel"
            className={aiFilled.has("notes") ? "border-brand-300 bg-brand-50/30" : ""}
          />

          <AnimatePresence>
            {ocrText && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-500 max-h-32 overflow-auto"
              >
                <p className="font-semibold mb-1">OCR-tekst:</p>
                {ocrText}
              </motion.div>
            )}
          </AnimatePresence>

          {saveError && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle size={27} className="shrink-0 mt-0.5" />
              {saveError}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => { setIsOpen(false); reset(); }} disabled={saving}>Annuleren</Button>
            <Button onClick={handleSave} loading={saving}>{editing ? "Wijzigingen opslaan" : "Uitgave opslaan"}</Button>
          </div>
        </div>
      </Modal>

      <PreviewModal
        isOpen={!!previewId}
        onClose={() => setPreviewId(null)}
        kind="expenses"
        id={previewId}
        title="Uitgave"
      />

      <ScanImportModal
        isOpen={scanOpen}
        onClose={() => setScanOpen(false)}
        mode="expense"
        onSaved={loadExpenses}
        onEditCreated={openEdit}
      />

      <Lightbox
        isOpen={!!lightbox}
        onClose={() => setLightbox(null)}
        src={lightbox?.src || ""}
        fileName={lightbox?.fileName}
      />
    </div>
  );
}
